import React from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { ComboBox, PhoneInputField, DatePicker, VATField } from '@fnol/library/src/components';
import { genderOptions, personalDocumentOptions, personType, riskTypeLife, translations } from '../../lib/consts';
import {
    GenericObject,
    question50MaxLength,
    questionMaxLength,
    constValues,
    surnameMaxLength,
    nameMaxLength,
    question25MaxLength,
} from '@fnol/library/src/lib';

type Props = {
    type: personType;
};

const LegalEntity = ({ type }: Props) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();
    const businessName = `${personType[type]}BusinessName`;
    const country = `${personType[type]}Country`;
    const countryText = `${personType[type]}MyCountry`;
    const vat = `${personType[type]}VAT`;
    const establishmentDate = `${personType[type]}EstablishmentDate`;
    const establishmentPlace = `${personType[type]}EstablishmentPlace`;
    const name = `${personType[type]}Name`;
    const lastName = `${personType[type]}LastName`;
    const docType = `${personType[type]}PersonalDocumentType`;
    const docNumber = `${personType[type]}PersonalDocumentNumber`;
    const issuePlace = `${personType[type]}DocumentIssuePlace`;
    const issueDate = `${personType[type]}DocumentIssueDate`;
    const gender = `${personType[type]}Gender`;
    const street = `${personType[type]}Street`;
    const municipality = `${personType[type]}Municipality`;
    const city = `${personType[type]}City`;
    const cityInSerbia = `${personType[type]}CityInSerbia`;
    const zipCode = `${personType[type]}ZipCode`;
    const phone = `${personType[type]}Phone`;
    const email = `${personType[type]}Email`;

    const selectedCountry = values.preparedCountries?.find(
        (item: GenericObject) => item.id === parseInt(values[country] as string),
    );
    values[countryText] = selectedCountry?.text;

    const handleChangeCountry = (event: SelectChangeEvent<HTMLSelectElement>) => {
        setFieldValue(country, event.target.value);
        if (!values.risks.includes(riskTypeLife.outlive)) {
            setFieldValue(street, '');
            setFieldValue(city, '');
            setFieldValue(cityInSerbia, '');
            setFieldValue(vat, '');
            setFieldValue(zipCode, '');
        }
    };

    return (
        <>
            <FormControl>
                <Field
                    name={businessName}
                    label={translations.legalInfo.BUSINESS_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: nameMaxLength,
                    }}
                    error={!!(touched[businessName] && errors[businessName])}
                    required
                />
                {touched[businessName] && errors[businessName] ? (
                    <FormHelperText error={true}>
                        <>{errors[businessName]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[country] && errors[country])} required>
                <InputLabel id={country + '-label'}>{translations.COUNTRY}</InputLabel>
                <Select
                    id={country}
                    labelId={country + '-label'}
                    name={country}
                    value={values[country] || ''}
                    label={translations.COUNTRY}
                    onChange={handleChangeCountry}
                >
                    {values.countries?.map((option: GenericObject) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.text}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl>
                <Field
                    name={vat}
                    id={vat}
                    label={translations.legalInfo.VAT}
                    as={VATField}
                    selectedCountry={values[country]}
                    error={!!(touched[vat] && errors[vat])}
                    required
                />
                {touched[vat] && errors[vat] ? (
                    <FormHelperText error={true}>
                        <>{errors[vat]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl
                error={!!(touched[establishmentDate] && errors[establishmentDate])}
                className="policyDatePicker"
            >
                <Field
                    name={establishmentDate}
                    label={translations.legalInfo.ESTABLISHMENT_DATE}
                    as={DatePicker}
                    disableFuture
                    error={!!(touched[establishmentDate] && errors[establishmentDate])}
                />
                {touched[establishmentDate] && errors[establishmentDate] ? (
                    <FormHelperText error={true}>
                        <>{errors[establishmentDate]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={establishmentPlace}
                    label={translations.legalInfo.ESTABLISHMENT_PLACE}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[establishmentPlace] && errors[establishmentPlace])}
                    required
                />
                {touched[establishmentPlace] && errors[establishmentPlace] ? (
                    <FormHelperText error={true}>
                        <>{errors[establishmentPlace]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <Typography variant="subtitle1">{translations.legalInfo.PERMANENT_RESIDENCE}</Typography>

            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                    required
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={municipality}
                    label={translations.MUNICIPALITY}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[municipality] && errors[municipality])}
                    required
                />
                {touched[municipality] && errors[municipality] ? (
                    <FormHelperText error={true}>
                        <>{errors[municipality]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[country] !== constValues.countrySerbiaId.toString(10) &&
            values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                        required
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                    zipCode: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                        required
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={zipCode}
                    label={translations.ZIP_CODE}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[zipCode] && errors[zipCode])}
                    required
                />
                {touched[zipCode] && errors[zipCode] ? (
                    <FormHelperText error={true}>
                        <>{errors[zipCode]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <PhoneInputField name={phone} specialLabel={translations.legalInfo.MOBILE} country={'rs'} required />
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    label={translations.personalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                    required
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={name}
                    label={translations.legalInfo.NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: nameMaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={lastName}
                    label={translations.legalInfo.LAST_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: surnameMaxLength,
                    }}
                    error={!!(touched[lastName] && errors[lastName])}
                    required
                />
                {touched[lastName] && errors[lastName] ? (
                    <FormHelperText error={true}>
                        <>{errors[lastName]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl required={true}>
                <InputLabel id="gender-type-label">{translations.legalInfo.GENDER}</InputLabel>
                <Select
                    id={gender}
                    labelId="gender-type-label"
                    name={gender}
                    value={values[gender] || ''}
                    label={translations.legalInfo.GENDER}
                    error={!!(touched[gender] && errors[gender])}
                    onChange={handleChange}
                >
                    {genderOptions.map((option) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[gender] && errors[gender] ? (
                    <FormHelperText error={true}>
                        <>{errors[gender]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl required={true}>
                <InputLabel id="personal-document-type-label">
                    {translations.legalInfo.SELECT_PERSONAL_DOC_TYPE}
                </InputLabel>
                <Select
                    id={docType}
                    labelId="personal-document-type-label"
                    name={docType}
                    value={values[docType] || ''}
                    label={translations.legalInfo.SELECT_PERSONAL_DOC_TYPE}
                    error={!!(touched[docType] && errors[docType])}
                    onChange={handleChange}
                >
                    {personalDocumentOptions.map((option) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[docType] && errors[docType] ? (
                    <FormHelperText error={true}>
                        <>{errors[docType]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={docNumber}
                    label={translations.legalInfo.PERSONAL_DOC_NUMBER}
                    as={TextField}
                    inputProps={{
                        maxLength: question25MaxLength,
                    }}
                    error={!!(touched[docNumber] && errors[docNumber])}
                    required
                />
                {touched[docNumber] && errors[docNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[docNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={issuePlace}
                    label={translations.legalInfo.ISSUE_PLACE}
                    as={TextField}
                    inputProps={{
                        maxLength: question25MaxLength,
                    }}
                    error={!!(touched[issuePlace] && errors[issuePlace])}
                    required
                />
                {touched[issuePlace] && errors[issuePlace] ? (
                    <FormHelperText error={true}>
                        <>{errors[issuePlace]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl error={!!(touched[issueDate] && errors[issueDate])} className="policyDatePicker">
                <Field
                    name={issueDate}
                    label={translations.legalInfo.ISSUE_DATE}
                    as={DatePicker}
                    disableFuture
                    error={!!(touched[issueDate] && errors[issueDate])}
                />
                {touched[issueDate] && errors[issueDate] ? (
                    <FormHelperText error={true}>
                        <>{errors[issueDate]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const LegalEntityCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue(`${personType[type]}BusinessName`, '');
    setFieldValue(`${personType[type]}Country`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}MyCountry`, '');
    setFieldValue(`${personType[type]}VAT`, '');
    setFieldValue(`${personType[type]}EstablishmentDate`, '');
    setFieldValue(`${personType[type]}EstablishmentPlace`, '');
    setFieldValue(`${personType[type]}Name`, '');
    setFieldValue(`${personType[type]}LastName`, '');
    setFieldValue(`${personType[type]}PersonalDocumentType`, '');
    setFieldValue(`${personType[type]}PersonalDocumentNumber`, '');
    setFieldValue(`${personType[type]}DocumentIssuePlace`, '');
    setFieldValue(`${personType[type]}DocumentIssueDate`, '');
    setFieldValue(`${personType[type]}Gender`, '');
    setFieldValue(`${personType[type]}Street`, '');
    setFieldValue(`${personType[type]}Municipality`, '');
    setFieldValue(`${personType[type]}City`, '');
    setFieldValue(`${personType[type]}CityInSerbia`, '');
    setFieldValue(`${personType[type]}ZipCode`, '');
    setFieldValue(`${personType[type]}Phone`, constValues.dialCodeSerbia);
    setFieldValue(`${personType[type]}Email`, '');
};

export default LegalEntity;
